import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// React Router Dom
import { Link } from "react-router-dom";

// Mui
import { Grid } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

// Interfaces
import { Participant } from "../../../interfaces/Participant";
import { Chapter, ChapterTopic } from "../../../interfaces/Chapter";

// Components
import Title from "../../general/Title";

// Hooks
import useDb from "../../../hooks/useDb";
import { useFetchUser } from "../../../hooks/useFetchUser";
import { getIconFromTopic, getTitleFromTopic } from "../../../utils/topic";

// Utils
import settings from "../../../settings.json";

// Assets
import { HiHeart, HiOutlineHeart } from "react-icons/hi";
import noResult from "../../../assets/icons/icon-no-result.png";

const TopicsFavourited: React.FC = () => {
    const { t } = useTranslation();
    const { currentParticipant } = useFetchUser();
    const participantRequest = useDb<Participant>("Participants", null, currentParticipant);
    const chapterRequest = useDb<Chapter>("Chapters", null, currentParticipant);

    // States
    const [participant, setParticipant] = useState<Participant | null>(null);
    const [chapters, setChapters] = useState<Chapter[]>([]);
    const [selectedTab, setSelectedTab] = useState<ChapterTopic>("Brain Overview");

    const fetchFavourites = () => {
        if (currentParticipant && currentParticipant.id) {
            return participantRequest.onSnapshot({
                callback: setParticipant,
                id: currentParticipant.id,
            });
        }
    };

    const getChapters = async () => {
        if (participant && participant.favourites && participant.favourites.length > 0) {
            const localChapters = [];
            for (const chapterId of participant.favourites) {
                localChapters.push(await chapterRequest.getById(chapterId));
            }

            setChapters(localChapters as Chapter[]);
        }
    };

    useEffect(() => {
        if (participant) getChapters();
    }, [participant]);

    useEffect(() => {
        const unsub = fetchFavourites();

        return () => {
            if (typeof unsub === "function") unsub();
        };
    }, []);

    const handleRemoveFavourite = async (c: Chapter) => {
        if (currentParticipant && currentParticipant.id && c.id && participant) {
            let list: string[] = [];
            if (participant.favourites) list = participant.favourites;
            // if already favourited
            if (list.includes(c.id)) {
                list = list.filter(chap => chap !== c.id);
            } else {
                list.push(c.id);
            }
            participantRequest.updateDoc({ ...participant, favourites: list } as Participant);
        }
    };

    interface Props {
        image: any;
        title: string;
        chapter: Chapter;
        topic: string | any;
    }

    const TopicCard: React.FC<Props> = ({ image, title, chapter, topic }) => {
        return (
            <Link to={`/chapter/${chapter.id}`}>
                <div className="taskCard">
                    <div className="taskCard__logo">
                        <img src={image} alt="" className="nofilterimg" />
                    </div>
                    <div className="taskCard__content">
                        <div className="taskCard__firstLine">
                            <h5>
                                {topic} <span className="taskCard__separation">|</span> Chapter {chapter.chapter}
                            </h5>
                        </div>
                        <h3>{title}</h3>
                    </div>
                    {participant && chapter && chapter.id && (
                        <div className="taskCard__topRightCorner taskCard__pointer" onClick={() => handleRemoveFavourite(chapter)}>
                            {participant.favourites.includes(chapter.id) ? (
                                <div className="taskCard__fullHearth">
                                    <HiHeart />
                                </div>
                            ) : (
                                <HiOutlineHeart />
                            )}
                        </div>
                    )}
                </div>
            </Link>
        );
    };

    return (
        <div className="topicsFavourited">
            <header>
                <Title>{t("favourites")}</Title>

                <Box sx={{ width: "100%" }}>
                    <Tabs
                        variant="fullWidth"
                        value={selectedTab}
                        onChange={(event: React.SyntheticEvent, newValue: ChapterTopic) => setSelectedTab(newValue)}
                        textColor="secondary"
                        indicatorColor="secondary"
                        aria-label="secondary tabs example"
                    >
                        {settings.app.topics.map(t => (
                            <Tab
                                value={t}
                                label={
                                    <Tooltip title={getTitleFromTopic(t as ChapterTopic)}>
                                        <IconButton>
                                            <img src={getIconFromTopic(t as ChapterTopic)} alt={t} style={{ maxWidth: 35 }} />
                                        </IconButton>
                                    </Tooltip>
                                }
                            />
                        ))}
                    </Tabs>
                </Box>

                <p style={{ marginTop: 20 }}>{t("favDesc")}</p>
            </header>
            <div className="tf__cardContainer" style={{ marginTop: 0 }}>
                <h5 style={{ paddingTop: 20 }}>{getTitleFromTopic(selectedTab)}</h5>
                {chapters && chapters.filter(c => c.topic === selectedTab).length ? (
                    chapters
                        .sort((a, b) => (a.chapter < b.chapter ? -1 : 1))
                        .map(c => {
                            if (c.topic === selectedTab) {
                                return (
                                    <Grid item xs={12} key={c.id}>
                                        <TopicCard
                                            image={getIconFromTopic(c.topic)}
                                            chapter={c}
                                            title={c.titleEN}
                                            topic={getTitleFromTopic(c.topic)}
                                        />
                                    </Grid>
                                );
                            }
                        })
                ) : (
                    <Grid item xs={12}>
                        <img src={noResult} alt="" />
                    </Grid>
                )}
            </div>
        </div>
    );
};

export default TopicsFavourited;
